/**
 * The audit log's vocabulary — every action and resource code a row can carry.
 *
 * The company's compliance page draws each code through a translation key
 * (`business.businessSettings.compliance.{action,resource}Labels.*`). The codes
 * used to be free strings at twenty-three insert sites, so a new one reached the
 * page as its raw key ("evidence_capture_read", "engagement_evidence_capture" —
 * BW-60), and the page's action filter offered "create / update / delete /
 * approval", which no row has ever carried: every filter came back empty.
 *
 * These arrays are the ONLY codes `audit_logs.action` / `.resource_type` can
 * hold — from the apps and from the ops scripts under scripts/ alike (`@tonnie/database` types the columns with them, so an unregistered code
 * does not compile), the filter the page offers, and what the locale coverage
 * spec proves has a label in every language.
 *
 * LEGACY codes are no longer written but live in stored rows, which the page
 * still shows; they stay here for as long as the retention job keeps them.
 */

export const AUDIT_ACTIONS = [
  'account.email_changed',
  'account.name_changed',
  /** Ops: scripts/recovery/account-name-drift.ts settles a Clerk ↔ platform name drift. */
  'account.name_reconciled',
  'affiliate.payout_cancelled',
  'affiliate.payout_invoice_attached',
  'affiliate.payout_requested',
  'affiliate.platform_overview_viewed',
  'communications.relinked_to_account',
  'evidence.blob_read',
  'evidence.blob_released',
  'evidence.capture_expired',
  'evidence.capture_read',
  'evidence.diff_viewed',
  'evidence.item_redacted',
  'feature_flags.write',
  'invitation.claimed',
  /** Ops: scripts/recovery/w5-d5-delete-orphan-pitch-videos.ts — an operator removed a citizen's orphaned file. */
  'ops.vault_orphan_delete',
  'pool.bulk_imported',
  'pool.candidate_ingested',
  'pool.cv_viewed',
  'service_credential.created',
  'service_credential.revoked',
  /** LEGACY — the pre-Clerk system-template dispatch record (see migration 0126). */
  'system_template.dispatched_pre_clerk',
  'talent.bulk_imported',
  'talent.invitation_created',
  'talent.invitation_declined',
  'talent.invitation_deleted',
  /** A send whose email was never dispatched — the hub refused it, or it failed. */
  'talent.invitation_email_failed',
  'talent.invitation_revoked',
  'talent.invitation_sent',
  'talent.invitation_superseded',
  'talent.invited_to_vetting',
] as const;

export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export const AUDIT_RESOURCE_TYPES = [
  'affiliate_payout',
  'affiliate_platform',
  'engagement_evidence_blob',
  'engagement_evidence_capture',
  'engagement_evidence_item',
  'journey_invitation',
  /** LEGACY — carried by `system_template.dispatched_pre_clerk` rows. */
  'message_template',
  'platform_configs',
  'service_credential',
  'talent',
  'tenant',
  'user',
  'vault_file',
] as const;

export type AuditResourceType = (typeof AUDIT_RESOURCE_TYPES)[number];

export function isAuditAction(value: string): value is AuditAction {
  return (AUDIT_ACTIONS as readonly string[]).includes(value);
}

/** One audit row as the company's compliance page (and its Companion) reads it. */
export interface AuditLogEntry {
  id: string;
  action: AuditAction;
  resourceType: AuditResourceType;
  resourceId: string | null;
  userId: string | null;
  userEmail: string | null;
  /** The recorded payload — what the action wrote or read. */
  newValues: Record<string, unknown> | null;
  /** ISO timestamp. */
  createdAt: string;
}

/**
 * The translation key each action is drawn with — full literal keys, so the
 * key-usage gate sees every one, `satisfies` makes a new action a compile error
 * until it has one, and the locale coverage spec proves each exists.
 */
export const AUDIT_ACTION_LABEL_KEY = {
  'account.email_changed':
    'business.businessSettings.compliance.actionLabels.account_email_changed',
  'account.name_changed': 'business.businessSettings.compliance.actionLabels.account_name_changed',
  'account.name_reconciled':
    'business.businessSettings.compliance.actionLabels.account_name_reconciled',
  'affiliate.payout_cancelled':
    'business.businessSettings.compliance.actionLabels.affiliate_payout_cancelled',
  'affiliate.payout_invoice_attached':
    'business.businessSettings.compliance.actionLabels.affiliate_payout_invoice_attached',
  'affiliate.payout_requested':
    'business.businessSettings.compliance.actionLabels.affiliate_payout_requested',
  'affiliate.platform_overview_viewed':
    'business.businessSettings.compliance.actionLabels.affiliate_platform_overview_viewed',
  'communications.relinked_to_account':
    'business.businessSettings.compliance.actionLabels.communications_relinked_to_account',
  'evidence.blob_read': 'business.businessSettings.compliance.actionLabels.evidence_blob_read',
  'evidence.blob_released':
    'business.businessSettings.compliance.actionLabels.evidence_blob_released',
  'evidence.capture_expired':
    'business.businessSettings.compliance.actionLabels.evidence_capture_expired',
  'evidence.capture_read':
    'business.businessSettings.compliance.actionLabels.evidence_capture_read',
  'evidence.diff_viewed': 'business.businessSettings.compliance.actionLabels.evidence_diff_viewed',
  'evidence.item_redacted':
    'business.businessSettings.compliance.actionLabels.evidence_item_redacted',
  'feature_flags.write': 'business.businessSettings.compliance.actionLabels.feature_flags_write',
  'invitation.claimed': 'business.businessSettings.compliance.actionLabels.invitation_claimed',
  'ops.vault_orphan_delete':
    'business.businessSettings.compliance.actionLabels.ops_vault_orphan_delete',
  'pool.bulk_imported': 'business.businessSettings.compliance.actionLabels.pool_bulk_imported',
  'pool.candidate_ingested':
    'business.businessSettings.compliance.actionLabels.pool_candidate_ingested',
  'pool.cv_viewed': 'business.businessSettings.compliance.actionLabels.pool_cv_viewed',
  'service_credential.created':
    'business.businessSettings.compliance.actionLabels.service_credential_created',
  'service_credential.revoked':
    'business.businessSettings.compliance.actionLabels.service_credential_revoked',
  'system_template.dispatched_pre_clerk':
    'business.businessSettings.compliance.actionLabels.system_template_dispatched_pre_clerk',
  'talent.bulk_imported': 'business.businessSettings.compliance.actionLabels.talent_bulk_imported',
  'talent.invitation_created':
    'business.businessSettings.compliance.actionLabels.talent_invitation_created',
  'talent.invitation_declined':
    'business.businessSettings.compliance.actionLabels.talent_invitation_declined',
  'talent.invitation_deleted':
    'business.businessSettings.compliance.actionLabels.talent_invitation_deleted',
  'talent.invitation_email_failed':
    'business.businessSettings.compliance.actionLabels.talent_invitation_email_failed',
  'talent.invitation_revoked':
    'business.businessSettings.compliance.actionLabels.talent_invitation_revoked',
  'talent.invitation_sent':
    'business.businessSettings.compliance.actionLabels.talent_invitation_sent',
  'talent.invitation_superseded':
    'business.businessSettings.compliance.actionLabels.talent_invitation_superseded',
  'talent.invited_to_vetting':
    'business.businessSettings.compliance.actionLabels.talent_invited_to_vetting',
} as const satisfies Record<AuditAction, string>;

/** The translation key each resource type is drawn with. */
export const AUDIT_RESOURCE_LABEL_KEY = {
  affiliate_payout: 'business.businessSettings.compliance.resourceLabels.affiliate_payout',
  affiliate_platform: 'business.businessSettings.compliance.resourceLabels.affiliate_platform',
  engagement_evidence_blob:
    'business.businessSettings.compliance.resourceLabels.engagement_evidence_blob',
  engagement_evidence_capture:
    'business.businessSettings.compliance.resourceLabels.engagement_evidence_capture',
  engagement_evidence_item:
    'business.businessSettings.compliance.resourceLabels.engagement_evidence_item',
  journey_invitation: 'business.businessSettings.compliance.resourceLabels.journey_invitation',
  message_template: 'business.businessSettings.compliance.resourceLabels.message_template',
  platform_configs: 'business.businessSettings.compliance.resourceLabels.platform_configs',
  service_credential: 'business.businessSettings.compliance.resourceLabels.service_credential',
  talent: 'business.businessSettings.compliance.resourceLabels.talent',
  tenant: 'business.businessSettings.compliance.resourceLabels.tenant',
  user: 'business.businessSettings.compliance.resourceLabels.user',
  vault_file: 'business.businessSettings.compliance.resourceLabels.vault_file',
} as const satisfies Record<AuditResourceType, string>;
